import { IntegrationTypes } from "../../../integrations/integration.service";
import { S25PricingUtil } from "../../s25.pricing.util";
import { PaymentSourceId } from "./s25.payments.store";

export namespace PaymentEmailBody {
    export const PAYMENT_LINK_TOKEN = /{{paymentLink\("(.*?)"\)}}/;
    export const PAYMENT_LINK_EXAMPLE = '{{paymentLink("here")}}';
    export const IDENTIFIER_TOKEN = "{{uniquePaymentIdentifier}}";
    export const AMOUNT_TOKEN = "{{paymentAmount}}";
    export const DUE_DATE_TOKEN = "{{paymentDueDate}}";
    export const TYPE_TOKEN = "{{paymentType}}";

    /** The link token is only worth mentioning when there is a portal for it to point at. */
    export function paymentTokenHelp(hasPortal: boolean): string {
        const linkHelp = hasPortal
            ? `${PAYMENT_LINK_EXAMPLE} becomes a link to the payment portal (only one link is used). `
            : "";
        return (
            linkHelp +
            `${IDENTIFIER_TOKEN} becomes the payment's identifier, ` +
            `and ${AMOUNT_TOKEN}, ${DUE_DATE_TOKEN} and ${TYPE_TOKEN} the figures from this request. ` +
            `BPE variables such as {{$pro.vars.event.itemName}} work in a template's message too.`
        );
    }

    export function usesPaymentLink(body: string): boolean {
        return PAYMENT_LINK_TOKEN.test(body);
    }

    /** What the payment tokens stand for, filled in from the request as it is sent. */
    export type PaymentTokenValues = {
        paymentUrl: string;
        /** Absent for a message with no payment behind it, where the token stands for nothing. */
        paymentDetailId: number | undefined;
        amount: string;
        dueDate: string;
        type: string;
        /**
         * Whether a message that names no link should still get one. True only for the default body,
         * where the link is the author's to delete. A template's message is governed by the compose
         * form's payment details checkbox instead, so nothing is added behind the author's back.
         */
        appendMissingLink: boolean;
    };

    export type PaymentEmailContext = {
        amount: number;
        orgName: string;
        eventName: string;
        sourceId: PaymentSourceId | undefined;
    };

    export function defaultPaymentEmailSubject(eventName: string): string {
        return `Payment Request for ${eventName}`;
    }

    export function defaultContactEmailSubject(orgName: string): string {
        return `Outstanding balance for ${orgName}`;
    }

    export function defaultContactEmailBody(context: { balance: number; orgName: string; eventName: string }): string {
        const { balance, orgName, eventName } = context;

        return (
            `<p>` +
            `<b>${orgName}</b> has an outstanding balance of ` +
            `<b>${S25PricingUtil.formatCurrency(balance)}</b> ` +
            `regarding billing for ` +
            `<b>${eventName}</b>.</p>`
        );
    }

    /**
     * What a 7 Point payer has to quote to be able to pay at all. Composed before the payment exists,
     * the identifier is still a token; resolved at send time, it is the payment's id.
     */
    function sevenPointInstructions(identifier: string, orgName: string): string {
        return (
            `<p>` +
            `Use the following unique payment identifier on the 7 Point Solutions payment portal: ` +
            `"${identifier}". Also use the following Customer Name: "${orgName}"` +
            `</p>`
        );
    }

    /**
     * What the processor needs the payer told, beyond whatever the author wrote. A processor that
     * collects online gets a link; 7 Point additionally needs the payer to quote an identifier,
     * because its portal is a shared address with no payment in the URL. A manual payment needs
     * neither, so it gets nothing.
     *
     * Only one link in a message is ever made live, so a message that already carries one is given
     * the block without it.
     */
    export function paymentDetailsBlock(
        sourceId: PaymentSourceId | undefined,
        orgName: string,
        includeLink = true,
    ): string {
        let block = "";

        if (sourceId === IntegrationTypes.sevenPoint) {
            block += sevenPointInstructions(IDENTIFIER_TOKEN, orgName);
        }

        if (includeLink && hasPaymentPortal(sourceId)) {
            block += `<p>Click ${PAYMENT_LINK_EXAMPLE} to make a payment.</p>`;
        }

        return block;
    }

    /** Only an integrated processor has a portal for a payment link to point at. */
    export function hasPaymentPortal(sourceId: PaymentSourceId | undefined): boolean {
        return sourceId === IntegrationTypes.stripe || sourceId === IntegrationTypes.sevenPoint;
    }

    const LINK_BLOCK_SELECTOR = "p, li, div, h1, h2, h3, h4, h5, h6, td";

    /**
     * Takes out the paragraph (or list item, heading, cell) that carries a payment link token, so a
     * message with nowhere to send the payer is not left saying "Payment link: here".
     */
    export function removePaymentLink(body: string): string {
        if (!PAYMENT_LINK_TOKEN.test(body)) return body;

        const container = document.createElement("template");
        container.innerHTML = body;

        const walker = document.createTreeWalker(container.content, NodeFilter.SHOW_TEXT);
        const tokenNodes: Text[] = [];
        while (walker.nextNode()) {
            const textNode = walker.currentNode as Text;
            if (PAYMENT_LINK_TOKEN.test(textNode.data)) tokenNodes.push(textNode);
        }

        for (const textNode of tokenNodes) {
            const block = textNode.parentElement?.closest(LINK_BLOCK_SELECTOR);
            (block ?? textNode).remove();
        }

        return container.innerHTML;
    }

    /** What a payment request says before anyone edits it. */
    export function defaultPaymentEmailBody(context: PaymentEmailContext): string {
        const { amount, orgName, eventName, sourceId } = context;

        const intro =
            `<p>` +
            `An invoice has been generated in the amount of ` +
            `<b>${S25PricingUtil.formatCurrency(amount)}</b> ` +
            `for ` +
            `<b>${orgName}</b> ` +
            `regarding billing for ` +
            `<b>${eventName}</b>.</p>`;

        return intro + paymentDetailsBlock(sourceId, orgName);
    }

    /**
     * Resolves the payment tokens the author may have used. A default-body message that names neither
     * the link nor the identifier still has to reach the payment portal, so a link is appended rather
     * than dropped; an author who named either is writing their own portal instructions and is left
     * to it. Only the first link token becomes a link; any later one is left as its wording.
     *
     * This runs after BPE interpolation, which only rewrites `{{$pro.vars.*}}` and so leaves these
     * tokens for us even when the message came from a template.
     */
    export function resolvePaymentTokens(body: string, values: PaymentTokenValues): string {
        const { paymentUrl, paymentDetailId, amount, dueDate, type, appendMissingLink } = values;
        const usesLink = PAYMENT_LINK_TOKEN.test(body),
            usesIdentifier = body.includes(IDENTIFIER_TOKEN);

        const linkableBody = paymentUrl ? body : removePaymentLink(body);
        const resolved = linkableBody
            .replace(PAYMENT_LINK_TOKEN, `<a href="${paymentUrl}">$1</a>`)
            .replace(new RegExp(PAYMENT_LINK_TOKEN.source, "g"), "$1")
            .replaceAll(IDENTIFIER_TOKEN, paymentDetailId ? `${paymentDetailId}` : "")
            .replaceAll(AMOUNT_TOKEN, amount)
            .replaceAll(DUE_DATE_TOKEN, dueDate)
            .replaceAll(TYPE_TOKEN, type);

        if (appendMissingLink && !usesLink && !usesIdentifier && paymentUrl) {
            return `${resolved}<p>Click <a href="${paymentUrl}">here</a> to make a payment.</p>`;
        }

        return resolved;
    }
}
